import Seo, { baseOrgSchema, websiteSchema } from '@/components/feature/Seo';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ContactForm from '@/pages/about/components/ContactForm';
import { siteConfig } from '@/config/site';

const stats = [
  { value: '2000+', label: '收录漫画', icon: 'ri-book-2-line' },
  { value: '6', label: '题材分类', icon: 'ri-grid-line' },
  { value: '500万+', label: '累计阅读', icon: 'ri-eye-line' },
  { value: '98%', label: '用户好评率', icon: 'ri-heart-line' },
];

const reviews = [
  {
    name: '小舟', role: '热血漫画爱好者',
    content: '漫画网的题材分类非常清晰，我按热血冒险很快就找到了喜欢的作品，追更体验也很棒！',
    avatar: 'https://readdy.ai/api/search-image?query=cute%20anime%20style%20avatar%20portrait%20of%20a%20young%20boy%20with%20warm%20smile%2C%20flat%20illustration%2C%20warm%20tones%2C%20clean%20background%2C%20friendly%20character&width=200&height=200&seq=avatar-yz-01&orientation=squarish',
  },
  {
    name: '云朵', role: '甜宠漫画迷',
    content: '最喜欢完结漫画板块，可以一口气看完，不用等更新。网站人气推荐也很准，完全对味！',
    avatar: 'https://readdy.ai/api/search-image?query=cute%20anime%20style%20avatar%20portrait%20of%20a%20cheerful%20girl%20with%20soft%20smile%2C%20flat%20illustration%2C%20soft%20pink%20tones%2C%20clean%20background%2C%20friendly%20character&width=200&height=200&seq=avatar-yd-02&orientation=squarish',
  },
  {
    name: '阿泽', role: '悬疑推理粉',
    content: '标签找书功能很实用，我点「悬疑」标签就能筛出所有推理漫画，省去了大量翻找时间。',
    avatar: 'https://readdy.ai/api/search-image?query=anime%20style%20avatar%20portrait%20of%20a%20cool%20young%20man%20with%20glasses%2C%20flat%20illustration%2C%20cool%20tones%2C%20clean%20background%2C%20confident%20character&width=200&height=200&seq=avatar-az-03&orientation=squarish',
  },
];

function About() {
  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于${siteConfig.name}`,
    url: `${siteConfig.url}/about`,
    description: `${siteConfig.name}是一家专注于在线漫画分类与推荐的中文阅读平台。`,
    inLanguage: 'zh-CN',
    about: baseOrgSchema,
  };

  return (
    <>
      <Seo
        title="关于我们"
        path="/about"
        description={`关于${siteConfig.name}：${siteConfig.tagline}。了解我们的品牌理念、用户口碑与联系方式，海量在线漫画分类推荐。`}
        jsonLd={[baseOrgSchema, websiteSchema, aboutSchema]}
      />

      <section className="relative mt-16 flex min-h-[260px] w-full items-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://readdy.ai/api/search-image?query=abstract%20warm%20artistic%20background%20with%20vermilion%20orange%20and%20cream%20ink%20wash%20texture%2C%20soft%20gradient%20swirls%2C%20minimal%20manga%20inspired%20design%2C%20subtle%20depth%2C%20editorial%20banner&width=1600&height=500&seq=about-banner-01&orientation=landscape')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 to-foreground-950/30"></div>
        <div className="relative mx-auto w-full max-w-7xl px-4 py-12 md:px-6">
          <Breadcrumb items={[{ name: '首页', path: '/' }, { name: '关于我们' }]} />
          <h1 className="mt-4 font-heading text-3xl font-bold text-background-50 md:text-4xl">关于我们</h1>
          <p className="mt-3 max-w-2xl text-sm text-background-100 md:text-base">
            用心做好漫画分类与推荐，让每一位读者都能快速找到心仪的漫画。
          </p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6" aria-labelledby="intro-title">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-primary-600">品牌介绍</span>
            <h2 id="intro-title" className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              关于{siteConfig.name}
            </h2>
            <p className="mt-5 text-sm leading-relaxed text-foreground-700 md:text-base">
              {siteConfig.name}是一家专注于中文漫画内容分类与推荐的在线阅读平台。我们深知，面对海量的漫画作品，读者最需要的是清晰、高效、可靠的找书方式。
            </p>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
              因此，我们围绕「题材分类、类型找书、标签筛选」三大核心能力，将完结漫画与连载中漫画分门别类，并引入人气推荐机制，让每一部优质作品都能被真正需要的读者发现。
            </p>
            <ul className="mt-6 space-y-3">
              {[
                { icon: 'ri-grid-fill', text: '六大题材分类，按类型精准找漫画' },
                { icon: 'ri-price-tag-3-fill', text: '热门标签找书，一键直达兴趣方向' },
                { icon: 'ri-flag-2-fill', text: '完结与连载中分门别类，追更不迷路' },
              ].map((f) => (
                <li key={f.text} className="flex items-center gap-3 text-sm text-foreground-700">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                    <i className={f.icon}></i>
                  </span>
                  {f.text}
                </li>
              ))}
            </ul>
          </div>
          <div className="overflow-hidden rounded-lg">
            <img
              src="https://readdy.ai/api/search-image?query=cozy%20anime%20reading%20corner%20with%20bookshelves%20full%20of%20manga%20and%20warm%20lighting%2C%20artistic%20illustration%2C%20warm%20vermilion%20and%20cream%20tones%2C%20inviting%20atmosphere%2C%20high%20detail&width=800&height=560&seq=about-intro-01&orientation=landscape"
              alt="漫画网阅读空间"
              title="漫画网在线阅读平台"
              className="h-full w-full object-cover"
            />
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-12 md:py-16" aria-label="平台数据">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {stats.map((s) => (
              <div key={s.label} className="rounded-lg bg-background-50 p-6 text-center">
                <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i className={`${s.icon} text-xl`}></i>
                </span>
                <p className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">{s.value}</p>
                <p className="mt-1 text-sm text-foreground-600">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6" aria-labelledby="review-title">
        <div className="mb-8 text-center">
          <span className="text-xs font-bold uppercase tracking-widest text-accent-700">用户口碑</span>
          <h2 id="review-title" className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">来自读者的真实好评</h2>
        </div>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          {reviews.map((r) => (
            <figure key={r.name} className="rounded-lg border border-background-200 bg-background-100 p-6">
              <div className="flex items-center gap-1 text-accent-500">
                {[1, 2, 3, 4, 5].map((n) => (
                  <i key={n} className="ri-star-fill text-sm"></i>
                ))}
              </div>
              <blockquote className="mt-4 text-sm leading-relaxed text-foreground-700">“{r.content}”</blockquote>
              <figcaption className="mt-5 flex items-center gap-3">
                <img src={r.avatar} alt={`${r.name} 头像`} className="h-10 w-10 rounded-full object-cover" />
                <div>
                  <p className="text-sm font-bold text-foreground-950">{r.name}</p>
                  <p className="text-xs text-foreground-600">{r.role}</p>
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section className="bg-background-100 py-14 md:py-16" aria-labelledby="contact-title">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-primary-600">联系我们</span>
              <h2 id="contact-title" className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">与我们取得联系</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700">
                无论是漫画合作、版权咨询还是意见反馈，欢迎随时联系我们。我们会在 1-2 个工作日内回复。
              </p>
              <ul className="mt-6 space-y-4">
                <li className="flex items-center gap-3">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-500 text-background-50">
                    <i className="ri-mail-line text-lg"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-600">电子邮箱</p>
                    <a href={`mailto:${siteConfig.email}`} className="text-sm font-bold text-foreground-950 hover:text-primary-600">{siteConfig.email}</a>
                  </div>
                </li>
                <li className="flex items-center gap-3">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-secondary-500 text-background-50">
                    <i className="ri-phone-line text-lg"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-600">联系电话</p>
                    <a href={`tel:${siteConfig.phone}`} className="text-sm font-bold text-foreground-950 hover:text-primary-600">{siteConfig.phone}</a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-accent-500 text-background-50">
                    <i className="ri-map-pin-line text-lg"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-600">公司地址</p>
                    <p className="text-sm font-bold text-foreground-950">{siteConfig.address}</p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
              <h3 className="text-lg font-bold text-foreground-950">在线留言</h3>
              <p className="mt-1 mb-6 text-sm text-foreground-600">填写下方表单，我们会尽快与您联系。</p>
              <ContactForm />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default About;